import { apiFetch } from './client';
import { getDeviceTokenHeader } from './auth';
import type { Paginated, RemoteLocation, RemoteWorkLog, WorkLog } from '$lib/types';

// RemotePunchFields is the optional field-work payload both punch endpoints
// accept (see handlers.remoteFields). Sending remote:true skips ONLY the
// server-side geofence check — device binding, the leave check and the
// double-punch check all still apply. lat/lng in the body are the
// coordinates parsed out of the pasted Google Maps link, not the device's
// own GPS fix.
export interface RemotePunchFields {
  remote?: boolean;
  location_name?: string;
  location_area?: string;
  maps_link?: string;
}

export interface PunchInBody extends RemotePunchFields {
  lat?: number;
  lng?: number;
  accuracy?: number;
  mood?: string;
  /** Current browser subscription, required server-side when Web Push is configured. */
  push_endpoint?: string;
}

export interface PunchOutBody extends RemotePunchFields {
  lat?: number;
  lng?: number;
  accuracy?: number;
  mood?: string;
}

export const workLogsApi = {
  list: (page = 1, perPage = 20) =>
    apiFetch<Paginated<WorkLog>>(`/work_logs?page=${page}&per_page=${perPage}`),
  // Anti-buddy-punching: the only two endpoints that require X-Device-Token
  // alongside the JWT (see DESIGN) — a stolen access token alone can't punch.
  punchIn: (body: PunchInBody) =>
    apiFetch<WorkLog>('/work_logs/punch_in', {
      method: 'POST',
      headers: getDeviceTokenHeader(),
      body: JSON.stringify(body)
    }),
  punchOut: (body: PunchOutBody) =>
    apiFetch<WorkLog>('/work_logs/punch_out', {
      method: 'POST',
      headers: getDeviceTokenHeader(),
      body: JSON.stringify(body)
    }),
  // The caller's own last five remote places, offered as quick-select chips.
  recentRemoteLocations: () =>
    apiFetch<{ locations: RemoteLocation[] }>('/work_logs/recent_remote_locations'),
  // HR+admin only (RequireHR) — everyone's remote punches, read-only.
  remoteList: (days = 30) =>
    apiFetch<{ work_logs: RemoteWorkLog[]; days: number }>(`/work_logs/remote?days=${days}`)
};
